/**
 * Properly encodes S3 URLs by encoding only the filename portion
 * while preserving the URL structure (protocol, domain, path)
 *
 * Now includes protection against double encoding
 *
 * @param url - The S3 URL to encode
 * @returns The properly encoded URL
 */
export const encodeS3Url = (url: string): string => {
  if (!url) return '';

  try {
    // Split the URL to get the filename (last part after the last slash)
    const lastSlashIndex = url.lastIndexOf('/');
    if (lastSlashIndex === -1) return url;

    const basePath = url.substring(0, lastSlashIndex + 1);
    const filename = url.substring(lastSlashIndex + 1);

    // Check if filename is already encoded by looking for URL-encoded characters
    const isAlreadyEncoded = /%[0-9A-Fa-f]{2}/.test(filename);

    if (isAlreadyEncoded) {
      // URL is already encoded (likely by backend transformer), return as-is
      return url;
    }

    // Only encode if filename contains unencoded special characters that need encoding
    const needsEncoding = /[+&()[\]{},%]/.test(filename);
    if (needsEncoding) {
      const encodedFilename = encodeURIComponent(filename);
      return basePath + encodedFilename;
    }

    // Clean URL that doesn't need encoding
    return url;
  } catch (error) {
    // Fallback to original URL if parsing fails
    console.error('Error encoding S3 URL:', error);
    return url;
  }
};
